import React, {
  useContext, useEffect, useMemo, useState,
} from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { compose } from 'recompose';
import { Autocomplete, Checkbox, TextField } from '@mui/material';
import moment from 'moment';
import format from 'string-template';

import Table from '../../../../components/v2/Table';
import { PrimaryButton } from '../../../../components/Button/ActionButtons';
import { Title, SubTitle } from '../../../../components/v2/Card';
import CoachesListContext from '../../../context/CoachesListContext';
import { DateFormat } from '../../../../utils/date';
import useActiveClientCounts from '../hooks/useActiveClientCounts';
import { getPoolCandidates } from '../utils/poolAssignments';
import {
  StyledModal,
  Container,
  AddSection,
  AddRow,
  AddButton,
  RosterCount,
  OptionLabel,
} from './styles';

import texts from './texts';

const ActiveClientsCount = ({ coachId, count, onRequest }) => {
  useEffect(() => {
    onRequest(coachId);
  }, [coachId, onRequest]);

  if (count === undefined) {
    return <>{texts.countLoading}</>;
  }

  return <>{count === null ? texts.countUnavailable : count}</>;
};

ActiveClientsCount.propTypes = {
  coachId: PropTypes.string.isRequired,
  count: PropTypes.number,
  onRequest: PropTypes.func.isRequired,
};

ActiveClientsCount.defaultProps = {
  count: undefined,
};

const PoolRosterModal = ({
  onClose,
  rosterRows,
  onAddCoachesToPool,
  onRemoveCoach,
  isSaving,
}) => {
  const { coachesCollection } = useContext(CoachesListContext);
  const [selectedCandidateIds, setSelectedCandidateIds] = useState([]);
  const { counts: activeClientCounts, requestCount } = useActiveClientCounts();

  const rosterCoachIds = rosterRows.map(({ coachId }) => coachId);

  const candidates = getPoolCandidates(coachesCollection.docs.slice(), rosterCoachIds);
  const selectedIds = new Set(selectedCandidateIds);
  const selectedCandidates = candidates.filter(({ id }) => selectedIds.has(id));

  const handleAddToPool = () => onAddCoachesToPool(selectedCandidates);

  const columns = useMemo(() => [
    {
      field: 'coachName',
      headerName: texts.tableColumns.coach,
      quickSearch: true,
      flex: 2,
    },
    {
      /*
        Counts resolve lazily, one request per row the grid actually renders, so at any moment most
        rows have no value yet. Sorting on that would order the roster by which rows happened to
        have loaded rather than by count, so the column is not sortable.
      */
      field: 'activeClients',
      headerName: texts.tableColumns.activeClients,
      flex: 1,
      sortable: false,
      valueGetter: ({ row }) => activeClientCounts[row.coachId],
      renderCell: ({ row, value }) => (
        <ActiveClientsCount
          coachId={row.coachId}
          count={value}
          onRequest={requestCount}
        />
      ),
    },
    {
      field: 'createdAt',
      headerName: texts.tableColumns.createdAt,
      flex: 1,
      renderCell: ({ row }) => moment(row.createdAt).format(DateFormat.DEFAULT_DATE_FORMAT),
    },
    {
      field: 'createdByUserName',
      headerName: texts.tableColumns.addedBy,
      quickSearch: true,
      flex: 1,
      renderCell: ({ row }) => row.createdByUserName || texts.unknownUser,
    },
    {
      field: 'actions',
      headerName: texts.tableColumns.actions,
      flex: 1,
      minWidth: 180,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <PrimaryButton
          onClick={() => onRemoveCoach({ id: row.coachId, name: row.coachName })}
          variant="info"
          size="medium"
          disabled={isSaving}
        >
          {texts.removeFromPool}
        </PrimaryButton>
      ),
    },
  ], [activeClientCounts, requestCount, onRemoveCoach, isSaving]);

  return (
    <StyledModal
      open
      onClose={onClose}
      title={texts.title}
      description={texts.description}
      maxWidth="lg"
      fullWidth
    >
      <Container>
        <AddSection>
          <div>
            <Title>{texts.addTitle}</Title>
            <SubTitle>{texts.addDescription}</SubTitle>
          </div>
          <AddRow>
            <Autocomplete
              options={candidates}
              value={selectedCandidates}
              onChange={(_, selection) => setSelectedCandidateIds(selection.map(({ id }) => id))}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              getOptionLabel={({ name }) => name}
              renderOption={(props, option, { selected }) => (
                <OptionLabel {...props} key={option.id}>
                  <Checkbox checked={selected} />
                  {option.name}
                </OptionLabel>
              )}
              renderInput={(params) => (
                <TextField {...params} placeholder={texts.addPlaceholder} />
              )}
              fullWidth
              multiple
              disableCloseOnSelect
            />
            <AddButton
              full={false}
              onClick={handleAddToPool}
              disabled={!selectedCandidates.length || isSaving}
            >
              {selectedCandidates.length
                ? format(texts.addCountToPool, { count: selectedCandidates.length })
                : texts.addToPool}
            </AddButton>
          </AddRow>
        </AddSection>
        <RosterCount>{format(texts.rosterCount, { count: rosterRows.length })}</RosterCount>
        <Table
          rows={rosterRows}
          columns={columns}
          disableRowSelectionOnClick
        />
      </Container>
    </StyledModal>
  );
};

PoolRosterModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  rosterRows: PropTypes.array.isRequired,
  onAddCoachesToPool: PropTypes.func.isRequired,
  onRemoveCoach: PropTypes.func.isRequired,
  isSaving: PropTypes.bool.isRequired,
};

export default compose(
  observer,
)(PoolRosterModal);
